import React from 'react'
import PropTypes from 'prop-types'

const Chip = ({ label, onRemove }) => {
  return (
    <span className="glg-chip">
      {label}
      {onRemove && <button onClick={onRemove} className="glg-chip-remove">✕</button>}
    </span>
  )
}

Chip.propTypes = {
  label: PropTypes.string.isRequired,
  onRemove: PropTypes.func,
}

/**
 * GlgChipsList — a controlled chip list with a header and clear action.
 *
 * @param {Object}   props
 * @param {Array<{ label: string, value: string }>} props.data - Items to display as chips.
 * @param {(value: string) => void}  props.onItemRemove   - Called with the item's value when its chip is dismissed.
 * @param {() => void}              props.onClear         - Called when the clear action is triggered.
 * @param {() => React.ReactNode}   [props.renderLeftTop]  - Renders the left header text. Defaults to "Selected".
 * @param {() => React.ReactNode}   [props.renderRightTop] - Renders the right header action. Defaults to a "Clear All" button.
 */
const GlgChipsList = ({
  data = [],
  onItemRemove,
  onClear,
  renderLeftTop,
  renderRightTop,
}) => {
  if (data.length === 0) {
    return null
  }

  const leftContent = renderLeftTop
    ? renderLeftTop()
    : <span className="glg-chips-list-title">
      {window.I18n.t('shared.components.selected')}
    </span>

  const rightContent = renderRightTop
    ? renderRightTop()
    : (
      <button className="glg-chips-list-clear" onClick={onClear}>
        {window.I18n.t('shared.components.clear_all')}
      </button>
    )

  return (
    <div className="glg-chips-list">
      <div className="glg-chips-list-header">
        {leftContent}
        {rightContent}
      </div>
      <div className="glg-chips-list-items">
        {data.map((item) => (
          <Chip
            key={item.value}
            label={item.label}
            onRemove={() => onItemRemove?.(item.value)}
          />
        ))}
      </div>
    </div>
  )
}

GlgChipsList.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onItemRemove: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  renderLeftTop: PropTypes.func,
  renderRightTop: PropTypes.func,
}

export default GlgChipsList
